/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { chain, includes, range, find, uniqBy } from "lodash";
import React, { useEffect } from "react";
import { Viewer } from "components/auth/types";
import { STATUSES_INACTIVE } from "model/interest/status";
import { formatAsCurrency } from "pages/dashboard/utils";
import { Button } from "components/haven-design";
import { SidebarFilters, SidebarFilters_interestListsById_sidebarFilters as SidebarFilter } from "types/gql-types";
import { useInterestsTable } from "../interests-table/table-context";
import { useSidebarFilters } from "../sidebar-filters";
import {
  AmiRadioInput,
  FilterCheckbox,
  FilterCheckboxWithInverse,
  FilterEqualityInput,
  FilterFacetCheckbox,
  FilterOver55,
  FilterRange,
  IncomeFilter,
} from "./filters";
import FilterStatuses from "./filters-statuses";
import Heading from "./heading";
import InterestListSelector from "./interest-list-selector";
import { FiltersState, sidebarState } from "./state";
import { interestListAmiRanges } from "./ami-ranges";
import Duplicates from "./duplicates";
// import { QUERY_SIDEBAR_FILTERS } from "./query";

const stripVerboseLabels = (text: string) =>
  chain(text)
    .replace("Does anyone in the household currently live", "Live")
    .replace("Are you a current", "Current")
    .replace("Are you currently", "Currently")
    .value();

const getCustomLabel = (filters: SidebarFilter[], name: string) => {
  const [filter] = filters.filter((x) => x.name === name);
  if (filter) return stripVerboseLabels(filter.label);
  return "";
};

const renderAllCustomPreferences =
(qty: number, filters: SidebarFilter[], filterList: (keyof FiltersState)[]) =>
  range(1, qty + 1).map((i) => {
    const name = `custom_pref_${i}` as keyof FiltersState;
    const customLabel = getCustomLabel(filters, name);
    const fallbackLabel = `Custom Preference #${i}`;
    const label = customLabel || fallbackLabel;
    return (
      <div key={i}>
        <FilterCheckboxWithInverse
          name={name} label={label}
          inverseLabel={`NOT ${label}`}
          filters={filterList}
        />
      </div>
    );
  });


interface Props {
  viewer: Viewer;
  data: SidebarFilters;
  listId: string;
}

const InterestsSidebarFilters: React.FC<Props> = ({
  listId, viewer, data: { interestListsById },
}) => {
  const { deselectAllInterests } = useInterestsTable();
  const {
    search,
    addFilter,
    clearFilters,
    setInterestListIds,
    interestListIds,
  } = useSidebarFilters();

  const sidebarFilters = uniqBy(interestListsById.map((x) => x.sidebarFilters).flat(), "name");
  const stages = uniqBy(interestListsById.map((x) => x.property.stages).flat().filter((stage) => stage.enabled), "name");
  const features = uniqBy(interestListsById.map((x) => x.property.features).flat(), "name");

  const availableStages = stages.map((stage) => stage.name);
  const activeStages = availableStages.filter((stage) =>
    !(STATUSES_INACTIVE as string[]).includes(stage));

  const currentState = (sidebarState as unknown) as FiltersState;
  const previousStages = currentState.available_statuses;

  useEffect(() => {
    if (!(previousStages.length === activeStages.length && previousStages
      .every((stage, i) => stage === activeStages[i]))) {
      // resetting the statuses if the stages changed
      // eslint-disable-next-line camelcase
      currentState.available_statuses = activeStages;
      currentState.statuses = activeStages;
      addFilter("status", activeStages, "array");
      search();
    }
  }, [previousStages, activeStages, interestListsById]);

  const filterList = sidebarFilters.map((x) => x.name) as (keyof FiltersState)[];
  const currentRange = listId === "all" ? false : find(interestListAmiRanges, (x) => x.interestListId === listId);
  const amiRanges = currentRange ? currentRange.amiRange : [];


  const doResetFilters = () => {
    clearFilters();
    sidebarState.reset();
    deselectAllInterests();
    const state = (sidebarState as unknown) as FiltersState;
    state.statuses = activeStages;
    addFilter("status", activeStages, "array");
    search();
  };
  const onListChange = (newListId: string) => {
    if (newListId === "all" && viewer.lists) {
      setInterestListIds(viewer.lists.map((list) => list.id));
    } else {
      setInterestListIds([newListId]);
    }
    doResetFilters();
  };

  const onResetFilters = () => doResetFilters();

  return (
    <div>
      <InterestListSelector
        viewer={viewer}
        onListChange={onListChange}
        interestListId={listId}
      />

      <div style={{ textAlign: "right", paddingTop: "1em" }}>
        {interestListIds.length === 1 && features.some((feature) => feature.name === "DUPLICATES") && (
          <Duplicates interestListId={listId} />
        )}
        <Button color="secondary" size="S" onClick={() => onResetFilters()}>
          Clear Filters
        </Button>
      </div>

      <h4>Status</h4>
      <FilterStatuses
        state={(sidebarState as unknown) as FiltersState}
        stages={availableStages}
        onChange={(statuses: string[]) => {
          const state = (sidebarState as unknown) as FiltersState;
          state.statuses = statuses;
          addFilter("status", statuses, "array");
          search();
        }}
      />

      {features.some((feature) => feature.name === "AMI_RANGE") && (
        <>
          <h4>AMI</h4>
          {amiRanges.map((amiRange, i) => (
            <div key={i}>
              <label style={{ marginRight: 6 }}>
                <AmiRadioInput label="Ami" name="ami" value={amiRange.value} amiRange={amiRange} filters={filterList} />
                {amiRange.value === "all" ? "All" : `${amiRange.value}% (${formatAsCurrency(amiRange.min)} - ${formatAsCurrency(
                  amiRange.max,
                )})`}
              </label>
            </div>
          ))}
        </>
      )}

      <Heading text="Household Income" name="heading_income" filters={filterList} />
      <IncomeFilter
        label="Income"
        name="household_income"
        filters={filterList}
        containerStyle={{ maxWidth: 230 }}
      />

      <Heading text="Household Size" name="heading_household_size" filters={filterList} />
      <FilterRange label="Household Size" name="household_count" filters={filterList} />

      <Heading text="Bedrooms" name="heading_bedrooms" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_sro" label="SRO" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_studio" label="Studio" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_1br" label="1 BR" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_1br_den" label="1 BR + Den" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_2br" label="2 BR" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_3br" label="3 BR" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_4br" label="4 BR" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_5br" label="5 BR" filters={filterList} />
      <FilterCheckbox name="filter_unit_size_shared" label="Shared" filters={filterList} />


      <h4 style={{ marginBottom: 2, marginTop: 25, fontSize: 14 }}>User Account</h4>
      <FilterCheckboxWithInverse
        showForEveryone
        name="has_user_id"
        label="Has Haven Connect account"
        inverseLabel="Does not have account"
        filters={filterList}
      />

      <h4 style={{ marginBottom: 2, marginTop: 25, fontSize: 14 }}>★ Starred</h4>
      <FilterCheckboxWithInverse
        showForEveryone
        name="has_star"
        label="Starred Applications"
        inverseLabel="Not Starred"
        filters={filterList}
      />

      {/* <h4 style={{ marginBottom: 2, marginTop: 25, fontSize: 14 }}>Document Uploads</h4>
      <FilterCheckboxWithInverse
        name="has_document"
        label="Applicant has documents"
        inverseLabel="Applicant does not have documents"
        filters={filterList}
      /> */}

      <Heading text="Subsidy" name="heading_subsidy" filters={filterList} />

      {filterList.includes("household_has_housing_authority_voucher") && (
        <FilterCheckboxWithInverse
          showForEveryone
          name="household_has_housing_authority_voucher"
          label="Has housing authority voucher"
          inverseLabel="No housing authority voucher"
          filters={filterList}
        />
      )}

      {filterList.includes("household_receives_other_housing_assistance") && (
        <FilterCheckboxWithInverse
          showForEveryone
          name="household_receives_other_housing_assistance"
          label="Has other housing assistance"
          inverseLabel="No other housing assistance"
          filters={filterList}
        />
      )}

      <Heading text="Preferences" name="heading_preferences" filters={filterList} />

      {renderAllCustomPreferences(14, sidebarFilters, filterList)}

      <FilterCheckbox
        name="household_requires_impairment_mobility"
        label="Mobility impairment"
        filters={filterList}
      />
      <FilterCheckbox
        name="household_requires_impairment_visual"
        label="Visual impairment"
        filters={filterList}
      />
      <FilterCheckbox
        name="household_requires_impairment_hearing"
        label="Hearing impairment"
        filters={filterList}
      />
      <FilterCheckbox
        name="house_member_wheelchair"
        label="Wheelchair Accessible"
        filters={filterList}
      />
      <FilterCheckbox
        name="household_lives_works_in_alameda"
        label="Live/Work in Alameda"
        filters={filterList}
      />
      <FilterCheckbox
        name="household_lives_in_palo_alto"
        label="Lives in Palo Alto"
        filters={filterList}
      />
      <FilterCheckbox
        name="household_works_in_palo_alto"
        label="Works in Palo Alto"
        filters={filterList}
      />
      {includes(filterList, "household_lives_works_in_san_carlos") && (
        <FilterCheckboxWithInverse
          showForEveryone
          name="household_lives_works_in_san_carlos"
          label="San Carlos"
          inverseLabel="NOT in San Carlos"
          filters={filterList}
        />
      )}
      <FilterCheckbox
        name="household_all_full_time_students"
        label="Full time student(s)"
        filters={filterList}
      />
      <FilterCheckbox
        name="household_served_in_armed_forces"
        label="Veteran"
        filters={filterList}
      />
      <FilterCheckbox
        name="household_has_special_accommodations"
        label="Has Special Accommodation"
        filters={filterList}
      />
      <FilterFacetCheckbox
        name="applicant_rental_buy_pref"
        facetValue="rent"
        label="Interested in Renting?"
        filters={filterList}
      />
      <FilterFacetCheckbox
        name="applicant_rental_buy_pref"
        facetValue="buy"
        label="Interested in Buying?"
        filters={filterList}
      />
      <FilterFacetCheckbox
        name="applicant_rental_buy_pref"
        facetValue="both"
        label="Interested in Both?"
        filters={filterList}
      />
      <Heading text="Applicant Age" name="heading_applicant_age" filters={filterList} />
      <FilterRange label="Date of Birth" name="applicant_dob" year filters={filterList} />
      <FilterOver55 label="Over 55" name="applicant_dob_over_55" filters={filterList} />

      <Heading text="AMI (excel imports only)" name="heading_ami" filters={filterList} />
      <div style={{ maxWidth: 55 }}>
        <FilterEqualityInput label="Ami" name="household_ami" filters={filterList} />
      </div>
      <FilterCheckbox name="ami_0br_40" label="0 BR 40%" filters={filterList} />
      <FilterCheckbox name="ami_0br_50" label="0 BR 50%" filters={filterList} />
      <FilterCheckbox name="ami_1br_40" label="1 BR 40%" filters={filterList} />
      <FilterCheckbox name="ami_1br_50" label="1 BR 50%" filters={filterList} />
      <FilterCheckbox name="ami_2br_40" label="2 BR 40%" filters={filterList} />
      <FilterCheckbox name="ami_2br_50" label="2 BR 50%" filters={filterList} />
      <FilterCheckbox name="ami_3br_40" label="3 BR 40%" filters={filterList} />
      <FilterCheckbox name="ami_3br_50" label="3 BR 50%" filters={filterList} />

      <Heading text="Position Number" name="heading_position_number" filters={filterList} />
      <FilterCheckbox
        name="has_position_number"
        label="Has Position Number"
        filters={filterList}
      />
      <FilterEqualityInput
        name="position_number"
        label="Lottery Position Number"
        filters={filterList}
        inputStyle={{ maxWidth: "60" }}
      />

      <Heading text="Gender" name="heading_gender" filters={filterList} />
      <FilterFacetCheckbox
        name="household_member_0_gender"
        facetValue="male"
        label="Male"
        filters={filterList}
      />
      <FilterFacetCheckbox
        name="household_member_0_gender"
        facetValue="female"
        label="Female"
        filters={filterList}
      />
      <FilterFacetCheckbox
        name="household_member_0_gender"
        facetValue="trans"
        label="Trans"
        filters={filterList}
      />
      <FilterFacetCheckbox
        name="household_member_0_gender"
        facetValue="non-binary"
        label="Non-Binary"
        filters={filterList}
      />
      <FilterFacetCheckbox
        name="household_member_0_gender"
        facetValue="other"
        label="Other"
        filters={filterList}
      />
    </div>
  );
};

export default InterestsSidebarFilters;
